import type { AdminQuoteGuest } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Button } from '@/shared/ui';
import { guestIssueNotice } from '../model/guest-issue-notice';
import { useIssueQuote } from '../model/use-issue-quote';

export interface IssueQuotePanelProps {
  readonly quoteRequestId: string;
  /** 화면에 보이는 현재 초안 id. */
  readonly versionId: string;
  /** 편집기가 불러왔거나 마지막으로 저장한 초안의 토큰 — 화면이 본 초안 그대로일 때만 서버가 발행한다. */
  readonly expectedDraftUpdatedAt: string;
  /** 편집기에 임시 저장하지 않은 변경이 있는지 — 서버는 저장된 초안으로 발행하므로 그동안 발행을 잠근다. */
  readonly isDirty: boolean;
  readonly onReloadLatest: () => void;
  /** 비회원 접수 요약 — 연결 전 비회원 견적이면 발행 뒤 링크가 가는 길을 안내한다(비회원 견적 설계 G14). 회원 견적은 null. */
  readonly guest?: AdminQuoteGuest | null;
}

const CONFIRM_MESSAGE = '이 초안을 발행하시겠습니까? 발행 후에는 이 차수를 수정할 수 없습니다.';
const DIRTY_NOTICE = '임시 저장하지 않은 변경이 있습니다. 임시 저장한 뒤 발행할 수 있습니다.';
const OUTDATED_NOTICE =
  '초안이나 견적 상태가 바뀌었습니다. 최신 초안을 다시 불러온 뒤 확인해 주세요.';

/**
 * 발행(견적 설계 §6-4) — 검증 실패(ISSUE_VALIDATION_FAILED)·발행 설정 미비(ISSUANCE_NOT_CONFIGURED)는
 * 서버 문구와 다음 행동 안내를, 충돌은 "최신 초안 다시 불러오기"를 보여 준다.
 * 연결 전 비회원 견적은 발행 영역과 확인창에 링크 전달 안내(이메일 자동 발송 / [링크 복사]로 카카오톡)를 더한다.
 */
export const IssueQuotePanel = ({
  quoteRequestId,
  versionId,
  expectedDraftUpdatedAt,
  isDirty,
  onReloadLatest,
  guest = null,
}: IssueQuotePanelProps): ReactElement => {
  const { issue, isPending, error, guidance, isOutdated } = useIssueQuote();
  const guestNotice = guestIssueNotice(guest);

  const handleIssue = (): void => {
    const message = guestNotice === null ? CONFIRM_MESSAGE : `${CONFIRM_MESSAGE}\n${guestNotice}`;

    if (!window.confirm(message)) {
      return;
    }

    void issue({ quoteRequestId, versionId, expectedDraftUpdatedAt });
  };

  return (
    <section aria-label="발행" className="flex flex-col gap-2 border-t pt-4">
      <h3 className="text-sm font-semibold">발행</h3>
      <p className="text-xs text-muted-foreground">
        발행하면 저장된 초안에 공급자 정보·수신자·공통 문구가 고정되고 고객에게 견적서가 보입니다.
      </p>
      {guestNotice === null ? null : <p className="text-sm font-medium">{guestNotice}</p>}

      {isDirty ? <p className="text-sm text-amber-700">{DIRTY_NOTICE}</p> : null}

      {error ? (
        <div role="alert" className="flex flex-col gap-1 text-sm text-destructive">
          <p>{error}</p>
          {guidance ? <p>{guidance}</p> : null}
        </div>
      ) : null}

      {isOutdated ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
        >
          <p>{OUTDATED_NOTICE}</p>
          <Button type="button" variant="outline" size="sm" onClick={onReloadLatest}>
            최신 초안 다시 불러오기
          </Button>
        </div>
      ) : null}

      <Button
        type="button"
        disabled={isDirty}
        isLoading={isPending}
        className="self-start"
        onClick={handleIssue}
      >
        발행
      </Button>
    </section>
  );
};
